import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import React, { CSSProperties, useEffect, useId } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatTime } from "utils/time-utils";
import { ComponentConfigDefault, HighlightMapped } from "models/dataset-models";
import { ReportOptions } from "models/component-print-models";
import { TitleOverrideAndDescriptionTemplate } from "../../templates/DataTableCellTemplates";
import { formatNumberWithGaps, getValueColors } from "../product-template-table/utils/utils";
import { DashboardComponentPropsBase } from "../../pages/dashboard/components/interfaces";

interface DatasetTableTransposedProps extends DashboardComponentPropsBase<ReportOptions> {
  data: Array<any>;
  config: ComponentConfigDefault;
  currentPage: number;
  highlightRanges: Array<HighlightMapped>;

  formatNumbers: boolean;

  getPageDates: (page: number) => Array<string>;
}

const DatasetTableTransposed = ({
  data,
  currentPage,
  getPageDates,
  config,
  highlightRanges,
  reportMode,
  formatNumbers
}: DatasetTableTransposedProps) => {
  const tableId = useId();

  useEffect(() => {
    if (config.hideDateColumn) {
      const table = document.getElementById(tableId);
      if (table) {
        const dateColumn = table.querySelector("thead");
        if (dateColumn) {
          dateColumn.style.display = "none";
        }
      }
    }
  }, [config]);

  const setColors = (date: string, obj: any): CSSProperties | undefined => {
    const objValue = obj[date];

    const id = highlightRanges.find(e => e.identifier === obj.identifier);

    const [color, bgColor] = getValueColors(objValue, {
      highlightRanges: id?.highlightRanges,
      defaultHighlightColor: id?.defaultHighlight,
    });
    return { color, backgroundColor: bgColor };
  };

  const columnHeaderBody = (row: any) => {
    const titleTemplate = TitleOverrideAndDescriptionTemplate({
      description: row.description,
      title: row.title,
      overriddenTitle: row.overriddenTitle,
      unit: row.unit,
      identifier: row.identifier,
    });
    // Maybe for the report mode (or all report component config) there should be provider context
    return (
      <div style={{ padding: "6px" }} className="flex gap-2">
        {titleTemplate}

        {reportMode ?? <ChartInDialogButton identifier={row.identifier} />}
      </div>
    );
  };

  const renderColumnBody = (e: any, date: string) => {
    const returnValue = formatNumbers ? formatNumberWithGaps(e[date]) : e[date];
    return (
      <div className="column-with-padding-for-color" style={setColors(date, e)}>
        {e[date] != null ? returnValue : "-"}
      </div>
    );
  };

  const valueColumnTemplate = (date: string) => {
    return (
      <Column
        header={formatTime(date)}
        className="column-dataset-table-transposed"
        key={date}
        field={date}
        body={(e) => renderColumnBody(e, date)}
        style={{ width: 0, whiteSpace: "nowrap" }}
      />
    );
  };

  return (
    <div className="data-table-wrapper transposed-table">
      <DataTable value={data} size="small" showGridlines id={tableId}>
        <Column
          className={
            config.hideDateColumn
              ? "column-into-header column-dataset-table-transposed"
              : "column-dataset-table-transposed"
          }
          header=""
          field="title"
          body={columnHeaderBody}
          style={{ whiteSpace: "nowrap" }}
        />
        {getPageDates(currentPage).map(date => valueColumnTemplate(date))}
      </DataTable>
    </div>
  );
};

export default DatasetTableTransposed;
